import type {
  QuestionImagepickerModelData,
  SurveyAnswerRecord,
  SurveyAnswerValue,
} from '@coorum-research/shared-common/types'
import type { SurveyModel } from 'survey-core'
import type { SurveyElementFormHandler } from './survey-element-form'
import { ValidationErrorMessages } from '@coorum-research/shared-common/shared/survey/survey-validation'
import { z } from 'zod'

/**
 * custom elementの場合は、validationをzodで行う。
 * 実際の入力値はSurveyResultRecordなので、validation実行時には変換が必要(@see getElementResultRecordForValidate)
 * schemaは、element毎に違いがあると思うのでここで、分岐して生成する。(@see createSurveyElementImagepickerSchema)
 */
function surveyElementSchemaFactory(elementJson: QuestionImagepickerModelData) {
  let valueSchema

  if (elementJson.multiSelect) {
    valueSchema = z.array(z.string())
  } else {
    valueSchema = z.string()
  }

  if (elementJson.isRequired) {
    valueSchema = valueSchema.min(1, { message: ValidationErrorMessages.REQUIRED })
  } else {
    valueSchema = valueSchema.optional()
  }

  return z.object({
    type: z.literal('imagepicker'),
    value: valueSchema,
    comment: z.string().optional(),
  })
}

export class SurveyElementFormHandlerImagepicker implements SurveyElementFormHandler {
  readonly #elementJson: QuestionImagepickerModelData
  #elementResultRecord: SurveyAnswerRecord | null
  readonly #formSchema: ReturnType<typeof surveyElementSchemaFactory>

  constructor(
    elementJson: QuestionImagepickerModelData,
    elementResultRecord: SurveyAnswerRecord | null,
  ) {
    this.#elementJson = elementJson
    this.#elementResultRecord = elementResultRecord
    this.#formSchema = surveyElementSchemaFactory(elementJson)
  }

  get elementJson(): QuestionImagepickerModelData {
    return this.#elementJson
  }

  get surveyModel(): SurveyModel | null {
    return null
  }

  get data(): SurveyAnswerRecord {
    return this.#elementResultRecord || {}
  }

  getValue(): SurveyAnswerValue {
    // const defaultValue = this.#elementJson.multiSelect ? [] : ''
    const defaultValue = ''
    return this.#elementResultRecord?.[this.#elementJson.name] || defaultValue
  }

  getComment(): string {
    return this.#elementResultRecord?.[`${this.#elementJson.name}-Comment`] as unknown as string || ''
  }

  setElementResultRecordValue(value: SurveyAnswerValue) {
    if (!this.#elementResultRecord) {
      this.#elementResultRecord = {}
    }

    this.#elementResultRecord[this.#elementJson.name] = value || ''
  }

  setElementResultRecordComment(comment: string) {
    if (!this.#elementResultRecord) {
      this.#elementResultRecord = {}
    }

    this.#elementResultRecord[`${this.#elementJson.name}-Comment`] = comment || ''
  }

  /**
   * SurveyResultRecordは objectで、validation schema(@see createSurveyElementSchema) は
   * { type, value, comment }なので validation用にdataを変換する
   * 本来は、二つが同じであればいいのだが、zod schemaを合わせられなかった。
   */
  _getElementResultRecordForValidate() {
    const defaultValue = this.#elementJson.multiSelect ? [] : ''

    return {
      type: this.#elementJson.type,
      value: this.#elementResultRecord?.[this.#elementJson.name] || defaultValue,
      comment: this.#elementResultRecord?.[`${this.#elementJson.name}-Comment`],
    }
  }

  validate(): {
    isValid: boolean
    errorMessage?: string
  } {
    const validationData = this._getElementResultRecordForValidate()
    const result = this.#formSchema.safeParse(validationData)

    let errorMessage: string = ''
    if (!result.success) {
      const errors = result.error.flatten()
      console.log(errors)
      errorMessage = errors.fieldErrors.value?.[0] || ValidationErrorMessages.GENERAL_ERROR
    }

    return {
      isValid: result.success,
      errorMessage,
    }
  }
}
